/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { UniversalTrackerPlain } from './universalTracker';
import { type UniversalTrackerValuePlain } from './universalTrackerValue';

export enum GenerationRunStatus {
  Pending = 'pending',
  Processing = 'processing',
  Completed = 'completed',
  Failed = 'failed',
}

export enum InsightStatus {
  PendingApproval = 'pending_approval',
  Approved = 'approved',
  Rejected = 'rejected',
  Pruned = 'pruned',
}

export enum GenerationEntityType {
  Insights = 'insights',
  Visualizations = 'visualizations',
  Narratives = 'narratives',
  Strategies = 'strategies',
}

export type SectionType = 'environment' | 'social' | 'governance' | 'economic';

export type InsightStatusAction = 'approved' | 'rejected';

// Union types for structured fields
export type Severity = 'info' | 'warning' | 'critical';
export type Priority = 'low' | 'medium' | 'high' | 'critical';
export type Effort = 'low' | 'medium' | 'high';

// New types for structured categorization (backend schema)
export type SwotCategory = 'strength' | 'weakness' | 'opportunity' | 'threat';
export type TimeHorizon = 'immediate' | 'short_term' | 'medium_term' | 'long_term';
export type ControlLevel = 'low' | 'medium' | 'high';

export interface ImpactAssessment {
  financial: number; // 0-10
  regulatory: number; // 0-10
  reputational: number; // 0-10
}

export interface InsightSource {
  categoryPath: string[];
  categoryName: string;
  metadata: {
    sectionType: SectionType;
    targetCode: string;
  };
}

export interface GenerationRunConfig {
  surveyIds?: string[];
  sectionType?: SectionType;
}

export interface GenerationRunProgress {
  total: number;
  completed: number;
  pending: number;
  approved: number;
  rejected: number;
  pruned: number;
  tokensUsed: number;
}

export interface GenerationRunError {
  code: string;
  message: string;
}

export interface GenerationRun {
  _id: string;
  batchId: string;
  initiativeId: string;
  entityType: GenerationEntityType;
  config: GenerationRunConfig;
  status: GenerationRunStatus;
  progress: GenerationRunProgress;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  lastRetryAt?: string;
  error?: GenerationRunError;
}

export interface KeyFinding {
  text: string;
  severity: Severity;
  confidence: number;
}

export type RecommendationPriority = 'low' | 'medium' | 'high' | 'critical';
export type RecommendationEffort = 'low' | 'medium' | 'high';

export interface Recommendation {
  title: string;                          // NEW: Structured title
  description: string;                    // NEW: Detailed description
  impactLevel: number;                    // NEW: 0-10 scale
  effortRequired?: number;                // NEW: 0-10 scale
  controlLevel: number;                   // NEW: 0-10 scale
  responsibleTeam: string;                // NEW: Team responsible for implementation
  timeHorizon: TimeHorizon;               // NEW: Implementation timeline
}

export interface MetricUtrvValue {
  surveyId: string;
  utrvId: string;
  utrvHistoryId: string;
  utrv: Pick<UniversalTrackerValuePlain, '_id' | 'value' | 'valueData' | 'valueType' | 'unit' | 'period' | 'effectiveDate' | 'status' | 'initiativeId'>;
}

export interface MetricReference {
  utrId: string;
  relevance: number;
  utr: UniversalTrackerPlain; // @TODO -Narrow this
  values: MetricUtrvValue[];
  promptContext?: string;
}

export interface InsightSynthesisContent {
  title: string;
  description: string;
  keyFindings: KeyFinding[];

  // Structured categorization
  swotCategory: SwotCategory;
  timeHorizon: TimeHorizon;
  controlLevel: ControlLevel;
  impactAssessment: ImpactAssessment;
  priorityScore: number; // 0-10
  likelihood?: 'high' | 'medium' | 'low';

  // Quality metrics
  impact: Priority;
  confidence: number; // 0-1
  relevance: number; // 0-1
  clarity: number; // 0-1

  // References and recommendations
  references: MetricReference[];
  recommendations: Recommendation[];
}

export interface InsightHistoryEntry {
  action: string;
  timestamp: string;
  userId?: string;
}

export interface InsightSynthesis {
  _id: string;
  batchId: string;
  batchInsightId: string; // Target-prefixed ID (e.g., 'climate-emissions_high-ghg')
  initiativeId: string;

  // AI model used to generate this insight (e.g. 'gpt-4o', 'gemini-2.5-flash')
  model: string;

  // Source attribution
  source: InsightSource;

  // Content
  content: InsightSynthesisContent;

  // Lifecycle
  status: InsightStatus;
  history: InsightHistoryEntry[];
  createdAt: string;
  updatedAt: string;
  approvedAt?: string;
  approvedBy?: string;
  rejectedAt?: string;
  rejectedBy?: string;
}

export interface CreateGenerationRunRequest {
  surveyIds?: string[];
  sectionTypes?: SectionType[];
  clientId: string;
  model?: string;
}

export interface CreateGenerationRunResponse {
  batchId: string;
  status: GenerationRunStatus;
  message: string;
}

/**
 * Response when retrying a generation run - returns the run info from AI service
 */
export interface RetryGenerationRunResponse {
  batchId: string;
  status: string;
  createdAt: string;
}
